import React from "react";
import { ClassNameProps, Column, If, Image, makeClassName, Row, StyleProps, TruncatedText } from "@hex-insights/core";
import { ExternalLink } from "@hex-insights/router";
import { Icon } from "../../../../Components/Icon";
import { MediaAttachment } from "../../../../Utilities";
import { MediaAttachmentFormValues } from "../../../FormUtilities";
import styles from "../file-link.module.css";

export type LinkAttachmentsRowProps = {
	attachments: Pick<MediaAttachment, "id" | "title" | "linkURL">[];
	attachmentClassName?: string;
	attachmentStyle?: React.CSSProperties;
} & Partial<ClassNameProps & StyleProps>;

export function LinkAttachmentsRow({
	attachments,
	attachmentClassName,
	attachmentStyle,
	className,
	style,
}: LinkAttachmentsRowProps) {
	return (
		<Row
			justify="spaced-start"
			horizontalSpacing="0.5rem"
			withVerticalSpacing
			verticalSpacing="0.5rem"
			overflow="wrap"
			className={className}
			style={style}
		>
			{attachments.map((attachment) => (
				<LinkAttachment
					key={attachment.id}
					attachment={attachment}
					className={makeClassName(styles["link-attachments-row__link-attachment"], attachmentClassName)}
					style={attachmentStyle}
				/>
			))}
		</Row>
	);
}

export type LinkAttachmentProps = Omit<LinkAttachmentViewProps, "title" | "linkURL"> & {
	attachment: Pick<MediaAttachment, "title" | "linkURL">;
};

export function LinkAttachment({ attachment, ...props }: LinkAttachmentProps) {
	return <LinkAttachmentView title={attachment.title} linkURL={attachment.linkURL} {...props} />;
}

export type LinkAttachmentPreviewProps = Omit<LinkAttachmentViewProps, "title" | "linkURL" | "noLink"> & {
	formValues: Pick<MediaAttachmentFormValues.Base, "title" | "linkURL">;
};

export function LinkAttachmentPreview({ formValues, ...props }: LinkAttachmentPreviewProps) {
	return <LinkAttachmentView title={formValues.title} linkURL={formValues.linkURL} noLink {...props} />;
}

type LinkAttachmentViewProps = {
	title: string;
	linkURL: string;
	noLink?: boolean;
	height?: string;
	onClick?: () => void;
} & Partial<ClassNameProps & StyleProps>;

function LinkAttachmentView({ title, linkURL, noLink, height, onClick, className, style }: LinkAttachmentViewProps) {
	// TODO preview content

	const content = (
		<Row justify="space-between" align="center" style={{ flexGrow: 1 }}>
			<Row justify="spaced-start" align="center" style={{ minWidth: 0, height: "100%" }}>
				<Image
					sizing="cover"
					fallbackContent={<LinkAttachmentImagePreviewFallback />}
					className={styles["link-attachment__image"]}
				/>

				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ overflow: "hidden" }}>
					<TruncatedText className={styles["link-attachment__name"]}>{title || "Link"}</TruncatedText>
					<TruncatedText className={styles["link-attachment__url"]}>
						{linkURL.replace(/^https?:\/\//, "")}
					</TruncatedText>
				</Column>
			</Row>

			<If condition={!noLink}>
				<Icon.ArrowSquareOut className={styles["link-attachment__external-icon"]} />
			</If>
		</Row>
	);

	const fullStyle = { "--attachment---height": height, ...style } as React.CSSProperties;

	if (noLink) {
		return (
			<div onClick={onClick} className={makeClassName(styles["link-attachment"], className)} style={fullStyle}>
				{content}
			</div>
		);
	}

	return (
		<ExternalLink href={linkURL} className={makeClassName(styles["link-attachment"], className)} style={fullStyle}>
			{content}
		</ExternalLink>
	);
}

function LinkAttachmentImagePreviewFallback() {
	return (
		<Row justify="center" align="center" className={styles["link-attachment__image-fallback"]}>
			<Icon.Link />
		</Row>
	);
}
